"use client"

import { useState, useSyncExternalStore } from "react"

import type { ReactNode } from "react"

import { useMountEffect } from "@/hooks/useMountEffect"
import { useModalStore } from "@/stores/modal/modal.store"

import { ModalShell } from "./ModalShell"
import {
  canonicalizeModalQuery,
  isModalName,
  MODAL_PARAMS,
  type ModalProps
} from "./registry"
import {
  closeUrlModal,
  getUrlSearch,
  subscribeUrlSearch,
  syncCanonicalModalSearch
} from "./url"
import { modalPropsFromSearch, MODAL_QUERY } from "./url-query"

export { ModalInstanceContext, useModalInstance } from "./instance-context"

export function ModalProvider({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false)
  useMountEffect(() => {
    setMounted(true)
  })

  return (
    <>
      {children}
      {mounted && (
        <>
          {/* URL first so a later state modal stacks above it in the DOM. */}
          <UrlModal />
          <StateModals />
        </>
      )}
    </>
  )
}

function StateModals() {
  const stack = useModalStore((state) => state.stack)
  const close = useModalStore((state) => state.close)
  const remove = useModalStore((state) => state.remove)

  return (
    <>
      {stack.map((modal) => (
        <ModalShell
          key={modal.key}
          open={!modal.closing}
          name={modal.name}
          props={modal.props}
          onClose={(result) => close(modal.key, result)}
          onExited={() => remove(modal.key)}
        />
      ))}
    </>
  )
}

function UrlModal() {
  const search = useSyncExternalStore(
    subscribeUrlSearch,
    getUrlSearch,
    () => ""
  )
  useMountEffect(() => {
    syncCanonicalModalSearch()
  })

  const name = new URLSearchParams(search).get(MODAL_QUERY)
  if (!isModalName(name)) return null

  const raw = modalPropsFromSearch(search)
  const canonicalize = canonicalizeModalQuery[name]
  const props = (
    canonicalize ? { ...raw, ...canonicalize(raw) } : raw
  ) as ModalProps[typeof name]

  return (
    <ModalShell
      name={name}
      props={props}
      onClose={() => closeUrlModal(MODAL_PARAMS[name])}
    />
  )
}
